import { useAppLocation } from "@/lib/app-navigation";
import emailjs from "@emailjs/browser";
import {
  isEmailJsConfigured,
  requireEmailJsConfig,
} from "@/config/emailjsConfig";
import { useAuth } from "@/platforms/customer/context/AuthContext";
import {
  getBlogs,
  transformBlogForFrontend,
} from "@/platforms/customer/services/blogService";
import {
  getPublishedProducts,
  transformProductForFrontend,
  recordProductClick,
  addRecentlyViewed,
  getProductClicks,
  getRecentlyViewed,
} from "@/platforms/customer/services/productService";
import { loadHomepageCatalog } from "@/platforms/customer/services/homepageCatalog";
import { useRouter } from "next/navigation";
import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { navigateToTourProduct } from "@/utils/tourUrl";
import {
  destinationsToHeroChips,
  listPublishedDestinations,
} from "@/platforms/customer/services/destinationService";

export const useLandingPage = () => {
  const location = useAppLocation();
  const heroRef = useRef(null);

  useEffect(() => {
    try {
      localStorage.setItem("tripsadora_mode", "customer");
    } catch (_) {}
  }, []);

  useEffect(() => {
    if (location.hash) {
      const id = location.hash.replace("#", "");
      setTimeout(() => {
        const el = document.getElementById(id);
        if (el) {
          if (id === "top-destinations") {
            const sectionEl = document.getElementById(
              "top-destinations-section",
            );
            if (sectionEl) {
              sectionEl.scrollIntoView({ behavior: "smooth", block: "start" });
              return;
            }
          }
          el.scrollIntoView({ behavior: "smooth", block: "start" });
        }
      }, 100);
    }
  }, [location.hash]);

  return { heroRef };
};

// hero section
export const useHeroSection = () => {
  const location = useAppLocation();
  const isLandingPage = location.pathname === "/";

  const [destination, setDestination] = useState("");
  const [theme, setTheme] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const router = useRouter();
  const searchTimeoutRef = useRef(null);
  const dropdownRef = useRef(null);

  // Debounced search function
  useEffect(() => {
    // Clear previous timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // If search query is empty, clear results
    if (!searchQuery.trim()) {
      setSearchResults([]);
      setShowDropdown(false);
      setIsSearching(false);
      return;
    }

    // Set loading state
    setIsSearching(true);
    setShowDropdown(true);

    // Debounce search - wait 300ms after user stops typing
    searchTimeoutRef.current = setTimeout(async () => {
      try {
        const data = await getPublishedProducts(
          {
            search: searchQuery.trim(),
            page: 1,
            page_size: 10, // Fetch 10 products, show 3 by default with scroll
          },
          false,
        );

        if (data && data.products && Array.isArray(data.products)) {
          const transformed = data.products
            .map(transformProductForFrontend)
            .filter((p) => p !== null && p.isAvailableForCustomer);
          setSearchResults(transformed);
        } else {
          setSearchResults([]);
        }
      } catch (error) {
        console.error("Error searching products:", error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    // Cleanup function
    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleSearch = () => {
    const params = new URLSearchParams();
    if (destination) params.set("q", destination);
    if (theme) params.set("category", theme);
    const qs = params.toString();
    router.push(`/search${qs ? `?${qs}` : ""}`);
  };

  const handleProductClick = (productId, product) => {
    setShowDropdown(false);
    setSearchQuery("");
    if (product && typeof product === "object") {
      navigateToTourProduct(router, {
        ...product,
        id: productId || product.id,
      });
      return;
    }
    // Bare id still works; detail page redirects to /tours/{slug}-{id}
    router.push(`/tours/${productId}`);
  };

  const handleViewAllResults = () => {
    setShowDropdown(false);
    router.push(`/search?q=${encodeURIComponent(searchQuery)}`);
  };

  return {
    isLandingPage,
    destination,
    setDestination,
    theme,
    setTheme,
    searchQuery,
    setSearchQuery,
    searchResults,
    isSearching,
    showDropdown,
    setShowDropdown,
    router,
    dropdownRef,
    handleSearch,
    handleProductClick,
    handleViewAllResults,
  };
};

// Where To Move
export const useWhereToMove = () => {
  const scrollRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const container = scrollRef.current;

    if (!container) return;

    const handleScroll = () => {
      if (!scrollRef.current) return;

      const scrollLeft = scrollRef.current.scrollLeft;
      const cardWidth = 92; // approx width including gap (w-20 + gap)

      const index = Math.round(scrollLeft / cardWidth);
      setActiveIndex(index);
    };

    container.addEventListener("scroll", handleScroll);

    return () => container.removeEventListener("scroll", handleScroll);
  }, []);

  return { scrollRef };
};

// Tours Section
export const useToursSection = () => {
  const router = useRouter();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [recent, setRecent] = useState([]);
  const blogTickerRef = useRef(null);
  const blogTickerRef2 = useRef(null);
  const [shuffledBlogs, setShuffledBlogs] = useState([]);

  const [isSortOpen, setIsSortOpen] = useState(false);
  const [sortKey, setSortKey] = useState("most_clicked");
  const sortRef = useRef(null);

  useEffect(() => {
    if (typeof window === "undefined" || typeof document === "undefined")
      return;

    // Ticket 2: shared homepage catalog (one GET …/products/published)
    const load = async ({ force = false } = {}) => {
      try {
        setLoading(true);
        const { products: list } = await loadHomepageCatalog({ force });
        setProducts(list);
        
        const rawRecent = getRecentlyViewed();
        const validRecent = rawRecent.filter((r) => list.some((p) => String(p.id) === String(r.id)));
        if (validRecent.length !== rawRecent.length) {
          localStorage.setItem("recently_viewed_products", JSON.stringify(validRecent));
        }
        setRecent(validRecent);
        
        setError("");
      } catch (e) {
        setError("Failed to load tours");
        setProducts([]);
        setRecent(getRecentlyViewed());
      } finally {
        setLoading(false);
      }
    };
    load();

    // Smooth revalidation when tab becomes visible instead of constant polling
    const onVisible = () => {
      if (document.visibilityState === "visible") load({ force: true });
    };
    document.addEventListener("visibilitychange", onVisible);
    // Availability / catalog updates — refetch lists (cache already invalidated)
    const handler = () => {
      load({ force: true });
    };
    const onStorage = (e) => {
      if (e.key === "tripsadora_availability_bust") load({ force: true });
    };
    window.addEventListener("realtime-products-update", handler);
    window.addEventListener("storage", onStorage);
    // Blog ticker from API only (TA-035)
    (async () => {
      try {
        const data = await getBlogs({ summary: 1 });
        const transformed = (data.blogs || [])
          .map(transformBlogForFrontend)
          .filter(Boolean);
        const latest = transformed.slice(0, 20);
        for (let i = latest.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [latest[i], latest[j]] = [latest[j], latest[i]];
        }
        setShuffledBlogs(latest);
      } catch (_) {
        setShuffledBlogs([]);
      }
    })();

    // Auto-scrolling ticker
    let rafId;
    const speed = 0.5; // pixels per frame
    const step = () => {
      const el = blogTickerRef?.current;
      if (el) {
        el.scrollLeft += speed;
        if (el.scrollLeft >= el.scrollWidth / 2) el.scrollLeft = 0;
      }
      const el2 = blogTickerRef2?.current;
      if (el2) {
        el2.scrollLeft += speed;
        if (el2.scrollLeft >= el2.scrollWidth / 2) el2.scrollLeft = 0;
      }
      rafId = requestAnimationFrame(step);
    };
    rafId = requestAnimationFrame(step);

    return () => {
      window.removeEventListener("realtime-products-update", handler);
      window.removeEventListener("storage", onStorage);
      document.removeEventListener("visibilitychange", onVisible);
      cancelAnimationFrame(rafId);
    };
  }, []);

  const toursDesktop = useMemo(() => {
    const clicks = getProductClicks();
    const parsed = products
      // Only show products that are currently available for customers
      .filter((t) => t.isAvailableForCustomer)
      .map((t) => ({
        ...t,
        priceValue: Number((String(t.price).match(/\$(\d+)/) || [])[1]) || 0,
        updatedAt: t.id,
        clicks: clicks[t.id] || 0,
      }));
    switch (sortKey) {
      case "price_asc":
        return [...parsed].sort((a, b) => a.priceValue - b.priceValue);
      case "date_new":
        return [...parsed].sort((a, b) => b.updatedAt - a.updatedAt);
      case "date_old":
        return [...parsed].sort((a, b) => a.updatedAt - b.updatedAt);
      case "price_desc":
        return [...parsed].sort((a, b) => b.priceValue - a.priceValue);
      case "most_clicked":
      default:
        return [...parsed].sort((a, b) => b.clicks - a.clicks);
    }
  }, [products, sortKey]);

  const toursMobile = useMemo(() => {
    const clicks = getProductClicks();
    const parsed = products
      // Only show products that are currently available for customers
      .filter((t) => t.isAvailableForCustomer)
      .map((t) => ({
        ...t,
        priceValue: Number((String(t.price).match(/\$(\d+)/) || [])[1]) || 0,
        updatedAt: t.id,
        clicks: clicks[t.id] || 0,
      }));

    return parsed;
  }, [products]);

  const handleCardClick = (tour) => {
    recordProductClick(tour.id);
    // Resort immediately based on updated clicks
    setProducts((prev) => [...prev]);
    // Update recently viewed list
    const updated = addRecentlyViewed(tour);
    setRecent(updated);
    // Navigate to product detail if available
    if (tour.id) {
      navigateToTourProduct(router, tour);
    }
  };
  return {
    loading,
    error,
    toursMobile,
    toursDesktop,
    /** Local recently-viewed snapshots (Ticket 1) — not the Explore catalog */
    recentlyViewed: recent,
    handleCardClick,
    isSortOpen,
    setIsSortOpen,
    sortRef,
    sortKey,
    setSortKey,
  };
};

// Top Countries Section Desktop
export const useTopCountriesSectionDesktop = () => {
  const [activeTab, setActiveTab] = useState("trending");
  const [products, setProducts] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const router = useRouter();
  const location = useAppLocation();
  const [items, setItems] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const countries = await listPublishedDestinations();
        if (!cancelled) setItems(destinationsToHeroChips(countries));
      } catch {
        if (!cancelled) setItems([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (location.hash === "#top-attractions") {
      setActiveTab("attractions");

      // Wait for DOM update after tab change
      setTimeout(() => {
        const section = document.getElementById("home-tabs-section");

        if (section) {
          const headerHeight = 100;

          const elementPosition =
            section.getBoundingClientRect().top + window.pageYOffset;

          const offsetPosition = elementPosition - headerHeight - 20;

          window.scrollTo({
            top: offsetPosition,
            behavior: "smooth",
          });
        }
      }, 100); // small delay
    }
  }, [location]);

  useEffect(() => {
    // Ticket 2: same shared homepage catalog as tours (no second page_size query)
    let cancelled = false;
    const load = async ({ force = false } = {}) => {
      try {
        setLoading(true);
        const { products: list } = await loadHomepageCatalog({ force });
        if (!cancelled) {
          setProducts(list);
          setError("");
        }
      } catch {
        if (!cancelled) {
          setError("Failed to load products");
          setProducts([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();

    const onRealtime = () => load({ force: true });
    const onStorage = (e) => {
      if (e.key === "tripsadora_availability_bust") load({ force: true });
    };
    window.addEventListener("realtime-products-update", onRealtime);
    window.addEventListener("storage", onStorage);
    return () => {
      cancelled = true;
      window.removeEventListener("realtime-products-update", onRealtime);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  const trendingProducts = useMemo(() => {
    return [...products]
      .filter((p) => p.isAvailableForCustomer)
      .sort((a, b) => (b.rating || 0) - (a.rating || 0))
      .slice(0, 4);
  }, [products]);

  const [attractionsList, setAttractionsList] = useState([]);

  useEffect(() => {
    let cancelled = false;
    const fetchAttractions = async () => {
      try {
        const { fetchAllAttractionsServer } = await import("@/app/actions/getAttractions");
        const list = await fetchAllAttractionsServer();
        if (!cancelled) {
          // Fallback if list is missing or wrapped
          const parsedList = Array.isArray(list) ? list : (list?.data || list?.attractions || []);
          setAttractionsList(parsedList);
        }
      } catch (e) {
        console.error("Failed to load attractions", e);
      }
    };
    fetchAttractions();
    return () => {
      cancelled = true;
    };
  }, []);

  const handleCardClick = (tour) => {
    recordProductClick(tour.id);
    // Resort immediately based on updated clicks
    setProducts((prev) => [...prev]);
    // Update recently viewed list
    const updated = addRecentlyViewed(tour);
    setRecent(updated);
    // Navigate to product detail if available
    if (tour.id) {
      navigateToTourProduct(router, tour);
    }
  };

  return {
    activeTab,
    setActiveTab,
    router,
    trendingProducts,
    handleCardClick,
    attractionsList,
    items,
  };
};

// Experience You Will Never Forget
export const useExperienceYouWillNeverForget = () => {
  const router = useRouter();
  const { isAuthenticated } = useAuth();

  const handleInviteEarnClick = () => {
    if (!isAuthenticated) {
      router.push("/login", { state: { from: "/refer-earn" } });
      return;
    }
    router.push("/refer-earn");
  };
  return { router, handleInviteEarnClick };
};

// Newsletter Section
export const useNewsletterSection = () => {
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!isEmailJsConfigured()) return;
    try {
      const { publicKey } = requireEmailJsConfig();
      emailjs.init(publicKey);
    } catch (error) {
      console.warn("Failed to initialize EmailJS for Newsletter:", error);
    }
  }, []);

  const validateEmail = (value) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!email.trim()) {
      toast.error("Please enter your email.");
      return;
    }

    if (!validateEmail(email)) {
      toast.error("Please enter a valid email address.");
      return;
    }

    if (!isEmailJsConfigured()) {
      toast.error(
        "Email is not configured. Set NEXT_PUBLIC_EMAILJS_SERVICE_ID, NEXT_PUBLIC_EMAILJS_TEMPLATE_ID, and NEXT_PUBLIC_EMAILJS_PUBLIC_KEY.",
      );
      return;
    }

    setIsSubmitting(true);
    try {
      const { serviceId, templateId, publicKey } = requireEmailJsConfig();

      const templateParams = {
        email: email,
        subject: "New Newsletter Subscription",
        message: `New subscription request from: ${email}`,
        // These can be mapped in the EmailJS dashboard template
      };

      await emailjs.send(serviceId, templateId, templateParams, publicKey);

      toast.success("Successfully subscribed to newsletter!");
      setEmail("");
    } catch (error) {
      console.error("Newsletter subscription error:", error);
      toast.error("Failed to subscribe. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return { email, setEmail, handleSubmit, isSubmitting };
};
